import { useRequest } from 'ahooks'
import { Message } from '@/components'
import { t } from '@/i18n'
import {
  addESUseCase,
  addMailAttachment,
  addMailUrlUseCase,
  addTpUseCase,
  addUseCase,
  AddUseCaseParams,
  addWgUseCase,
  batchImportUseCase,
  BatchImportUseCaseParams,
  batchImportWgUseCase,
  createMailAttachmentUseCase,
  deleteUseCase,
  editESUseCase,
  editMailAttachmentUseCase,
  editMailUrlUseCase,
  editTpUseCase,
  editUseCase,
  EditUseCaseParams,
  editWgUseCase,
  getAllAttackType,
  getAttackOrganizationList,
  getAttackTypes,
  getComponentList,
  getESUseCaseDetail,
  getMailAttachmentUseCaseDetail,
  getMailUrlUseCaseDetail,
  getTacticsList,
  getTpUseCaseDetail,
  getUseCaseDetail,
  getUseCaseDetailWebSecurity,
  getUseCaseIds,
  getUseCaseList,
  GetUseCaseListParams,
  getUseCaseTagList,
  getWgUseCaseDetail,
  uploadMaliciousFile,
} from '@/api/useCase-knowledge-base/useCase-center'

// 获取用例详情（web安全）
export const useGetUseCaseDetailWebSecurity = () => {
  const { runAsync: run, loading } = useRequest((id: string) => getUseCaseDetailWebSecurity(id), {
    manual: true,
  })
  return [run, loading] as const
}

// 获取用例详情（仅公共属性） (web安全)
export const useGetUseCaseDetail = () => {
  const { runAsync: run, loading } = useRequest((id: string) => getUseCaseDetail(id), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取用例详情失败'))
    },
  })

  return [run, loading] as const
}

// 获取用例列表
export const useGetUseCaseList = () => {
  const { runAsync: run, loading } = useRequest(
    (params: GetUseCaseListParams) => getUseCaseList(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例列表失败'))
      },
    },
  )

  return [run, loading] as const
}

// 删除用例 (web安全)
export const useDeleteUseCase = () => {
  const { runAsync: run, loading } = useRequest(
    (resourceIds: string[]) => deleteUseCase(resourceIds),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('删除用例失败'))
      },
    },
  )

  return [run, loading] as const
}

// 添加用例 (web安全)
export const useAddUseCase = () => {
  const { runAsync: run, loading } = useRequest((params: AddUseCaseParams) => addUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑用例
export const useEditUseCase = () => {
  const { runAsync: run, loading } = useRequest(
    (params: EditUseCaseParams) => editUseCase(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('编辑用例失败'))
      },
    },
  )

  return [run, loading] as const
}

// 获取用例ids
export const useGetUseCaseIds = () => {
  const { runAsync: run, data } = useRequest(
    (params: GetUseCaseListParams) => getUseCaseIds(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例ids失败'))
      },
    },
  )

  return [run, data?.data?.resourceIds || []] as const
}

// 获取攻击组织列表
export const useGetAttackOrganizationList = () => {
  const { runAsync: run, data } = useRequest(getAttackOrganizationList, {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取攻击组织列表失败'))
    },
  })

  return [run, data?.data?.aptList || []] as const
}

// 获取影响组件列表
export const useGetComponentList = () => {
  const { runAsync: run, data } = useRequest((params: any) => getComponentList(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取影响组件列表失败'))
    },
  })

  return [run, data?.data?.componentList || []] as const
}

// 获取用例标签列表
export const useGetUseCaseTagList = () => {
  const { runAsync: run, data } = useRequest((params: any) => getUseCaseTagList(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取用例标签列表失败'))
    },
  })

  return [run, data?.data?.categoryList || []] as const
}

// 获取战术列表
export const useGetTacticsList = () => {
  const { runAsync: run, data } = useRequest((params: any) => getTacticsList(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取战术列表失败'))
    },
  })

  return [run, data?.data?.tactics || []] as const
}

// 批量导入用例
export const useBatchImportUseCase = () => {
  const { runAsync: run, loading } = useRequest(
    (params: BatchImportUseCaseParams) => batchImportUseCase(params),
    {
      manual: true,
    },
  )

  return [run, loading] as const
}

// 批量导入用例
export const useBatchImportWgUseCase = () => {
  const { runAsync: run, loading } = useRequest(
    (params: BatchImportUseCaseParams) => batchImportWgUseCase(params),
    {
      manual: true,
    },
  )

  return [run, loading] as const
}

// 添加用例（终端安全）
export const useAddESUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => addESUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑用例（终端安全）
export const useEditESUseCase = () => {
  const { runAsync: run, loading } = useRequest((params: any) => editESUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('编辑用例失败'))
    },
  })

  return [run, loading] as const
}

// 获取用例详情（终端安全）
export const useGetESUseCaseDetail = () => {
  const { runAsync: run, data } = useRequest(
    (resourceId: string) => getESUseCaseDetail(resourceId),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例详情失败'))
      },
    },
  )

  return [run, data?.data || {}] as const
}

// 添加邮件安全附件
export const useAddMailAttachment = () => {
  const { runAsync: run, loading } = useRequest(params => addMailAttachment(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加邮件安全附件失败'))
    },
  })

  return [run, loading] as const
}

// 创建邮件安全附件用例
export const useCreateMAUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => createMailAttachmentUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('创建邮件安全附件用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑邮件安全附件用例
export const useEditMAUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => editMailAttachmentUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('编辑邮件安全附件用例失败'))
    },
  })

  return [run, loading] as const
}

// 获取邮件安全附件用例详情
export const useGetMAUseCaseDetail = () => {
  const { runAsync: run, data } = useRequest(
    (resourceId: string) => getMailAttachmentUseCaseDetail(resourceId),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取邮件安全附件用例详情失败'))
      },
    },
  )

  return [run, data?.data || {}] as const
}

// 添加邮件安全URL用例
export const useAddMailUrlUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => addMailUrlUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加邮件安全URL用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑邮件安全URL用例
export const useEditMailUrlUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => editMailUrlUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('编辑邮件安全URL用例失败'))
    },
  })

  return [run, loading] as const
}

// 获取邮件安全URL用例详情
export const useGetMailUrlUseCaseDetail = () => {
  const { runAsync: run, data } = useRequest(
    (resourceId: string) => getMailUrlUseCaseDetail(resourceId),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取邮件安全URL用例详情失败'))
      },
    },
  )

  return [run, data?.data || {}] as const
}

// 添加流量安全用例
export const useAddTpUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => addTpUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加流量安全用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑流量安全用例
export const useEditTpUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => editTpUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('编辑流量安全用例失败'))
    },
  })

  return [run, loading] as const
}

// 获取流量安全用例详情
export const useGetTpUseCaseDetail = () => {
  const { runAsync: run, data } = useRequest((id: string) => getTpUseCaseDetail(id), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取流量安全用例详情失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 恶意文件上传文件
export const useUploadMaliciousFile = () => {
  const { runAsync: run, loading } = useRequest(params => uploadMaliciousFile(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('恶意文件上传文件失败'))
    },
  })

  return [run, loading] as const
}

// 添加威胁情报用例
export const useAddWgUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => addWgUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('添加威胁情报用例失败'))
    },
  })

  return [run, loading] as const
}

// 编辑威胁情报用例
export const useEditWgUseCase = () => {
  const { runAsync: run, loading } = useRequest(params => editWgUseCase(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('编辑威胁情报用例失败'))
    },
  })

  return [run, loading] as const
}

// 获取威胁情报用例详情
export const useGetWgUseCaseDetail = () => {
  const { runAsync: run, data } = useRequest((id: string) => getWgUseCaseDetail(id), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取威胁情报用例详情失败'))
    },
  })

  return [run, data?.data || {}] as const
}

// 获取全场景攻击类型
export const useGetAllAttackType = () => {
  const { runAsync: run, data } = useRequest(getAllAttackType, {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取全场景攻击类型失败'))
    },
  })

  return [run, data?.data?.list || []] as const
}

// 获取全部攻击类型
export const useGetAttackTypes = () => {
  const { runAsync: run, data } = useRequest(getAttackTypes, {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || '获取全部攻击类型失败')
    },
  })

  return [run, data?.data?.attackTypeFilters || []] as const
}
